import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import ContractForm from '../components/ContractForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Paper,
    List,
    ListItemButton,
    ListItemText,
    Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';

function ContractsPage() {
    const [formOpen, setFormOpen] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState(null);
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Load user's company
    const { data: company, isLoading: companyLoading } = useQuery(
        'myCompany',
        () => JobHttpService.loadMyCompany()
    );

    // Load contracts for the company
    const { data: contractsData, isLoading: contractsLoading, isError, error } = useQuery(
        ['contracts', company?.id],
        () => {
            if (!company?.id) return { results: [], count: 0 };
            return JobHttpService.loadContracts({ company: company.id });
        },
        {
            enabled: !!company?.id,
            keepPreviousData: true
        }
    );

    const contracts = contractsData?.results || contractsData || [];

    const handleAddClick = () => {
        setSelectedContractId(null);
        setFormOpen(true);
    };

    const handleItemClick = (contractId) => {
        navigate(`/employer/contracts/${contractId}`);
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setSelectedContractId(null);
    };

    const handleContractSaved = () => {
        queryClient.invalidateQueries(['contracts', company?.id]);
    };

    const handleSaveAndAddAnother = () => {
        queryClient.invalidateQueries(['contracts', company?.id]);
    };

    if (companyLoading || contractsLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    if (!company) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        You need to create a company first. <Button href="/employer/my-company">Go to My Company</Button>
                    </Alert>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={600} gutterBottom>
                            Contracts
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage your company employment contracts
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Contract
                    </Button>
                </Box>

                {isError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error?.message || 'Failed to load contracts'}
                    </Alert>
                )}

                {contracts.length === 0 && !contractsLoading && (
                    <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                        <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No contracts found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Start by adding your first contract
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                        >
                            Add Contract
                        </Button>
                    </Paper>
                )}

                {contracts.length > 0 && (
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <List>
                                {contracts.map((contract, index) => {
                                    const employee = contract.employee;
                                    const employeeName = employee 
                                        ? `${employee.first_name || ''} ${employee.last_name || ''}`.trim()
                                        : 'Unknown Employee';
                                    const startDate = contract.contract_start_date 
                                        ? new Date(contract.contract_start_date).toLocaleDateString()
                                        : '';
                                    const endDate = contract.contract_end_date 
                                        ? new Date(contract.contract_end_date).toLocaleDateString()
                                        : '';
                                    const position = contract.employee_position_title || '—';

                                    return (
                                        <React.Fragment key={contract.id}>
                                            <ListItemButton onClick={() => handleItemClick(contract.id)}>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body1" fontWeight={500}>
                                                                {employeeName}
                                                            </Typography>
                                                            {contract.contract_number && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    ({contract.contract_number})
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                                            {position !== '—' && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {position}
                                                                </Typography>
                                                            )}
                                                            {startDate && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Start: {startDate}
                                                                </Typography>
                                                            )}
                                                            {endDate && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    End: {endDate}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <ArrowForwardIcon color="action" />
                                            </ListItemButton>
                                            {index < contracts.length - 1 && <Divider />}
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        </CardContent>
                    </Card>
                )}

                {contracts.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={handleAddClick}
                            sx={{ color: 'primary.main' }}
                        >
                            Add another contract
                        </Button>
                    </Box>
                )}

                {/* Contract Form Modal */}
                <ContractForm
                    open={formOpen}
                    onClose={handleFormClose}
                    contractId={selectedContractId}
                    companyId={company.id}
                    onSaved={handleContractSaved}
                    onSaveAndAddAnother={handleSaveAndAddAnother}
                />
            </Container>
        </section>
    );
}

export default ContractsPage;
